import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Check, ShieldCheck, Truck, RotateCcw, Wand2, Upload, Loader2 } from "lucide-react";
import { API, getAvenoVideos } from "@/lib/api";
import { addItem } from "@/lib/shopifyCart";
import { formatMoney } from "@/components/shop/ShopifyProducts";
import CartDrawer from "@/components/shop/CartDrawer";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

const AVENO_DESIGNS = ["Mark", "Wordmark", "Horizon", "Grid", "Orbit", "Wave", "Terra", "My own artwork"];

/**
 * Product detail — full description + details before adding to cart.
 * Data comes from the same cached Shopify feed as the Shop page.
 */
export default function ProductDetail() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [added, setAdded] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDesign, setCustomDesign] = useState(null);
  const [customText, setCustomText] = useState("");
  const [artwork, setArtwork] = useState(null); // { url, name }
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [videos, setVideos] = useState(null); // { vertical, landscape } cinematic ads for this product

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setVideos(null);
    window.scrollTo(0, 0);
    fetch(`${API}/shopify/products`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setNotFound(true));
    getAvenoVideos(handle).then(setVideos);
  }, [handle]);

  const product = data?.products?.find((p) => p.handle === handle);

  // Handle shape: aveno-{type}-{design} — lets us find colorways (same type,
  // other designs) and family (same design, other types).
  const parts = handle.split("-");
  const isAveno = parts[0] === "aveno" && parts.length >= 3;
  const designKey = isAveno ? parts[parts.length - 1] : null;
  const typeKey = isAveno ? parts.slice(1, -1).join("-") : null;
  const pretty = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const colorways = isAveno && data
    ? data.products.filter((p) => p.handle !== handle && p.handle.startsWith(`aveno-${typeKey}-`))
    : [];
  const family = isAveno && data
    ? data.products.filter((p) => p.handle !== handle && p.handle.endsWith(`-${designKey}`) && !p.handle.startsWith(`aveno-${typeKey}-`)).slice(0, 4)
    : [];

  useEffect(() => {
    if (data && !product) setNotFound(true);
  }, [data, product]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 md:px-12 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl mb-4">Product not found</h1>
        <p className="text-slate-600 mb-8">This product may have sold out or moved.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 text-primary font-semibold link-underline">
          <ArrowLeft className="w-4 h-4" /> Back to the shop
        </Link>
      </div>
    );
  }

  if (!product) {
    // Mount SEO immediately (handle-derived) so prerender snapshots get the right
    // URL + sensible title even if captured before product data loads.
    const handleTitle = handle.replace(/^aveno-/, "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    return (
      <div className="py-32 flex justify-center" data-testid="product-loading">
        <SEO title={`${handleTitle} — AVENO Shop`} description="Shop AVENO — family-founded apparel, kids and everyday tech." path={`/shop/product/${handle}`} />
        <div className="w-8 h-8 border-2 border-slate-200 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const variant = product.variants.find((v) => v.available) || product.variants[0];

  const customization = customOpen && (customDesign || customText.trim() || artwork)
    ? {
        design: customDesign || pretty(designKey),
        text: customText.trim() || undefined,
        artworkUrl: artwork ? `${window.location.origin}${artwork.url}` : undefined,
      }
    : null;

  const uploadArtwork = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Artwork must be under 15 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/shopify/upload-artwork`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      const d = await res.json();
      setArtwork({ url: d.url, name: file.name });
      setCustomDesign("My own artwork");
      toast.success("Artwork uploaded — it will travel with your order");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = () => {
    addItem(product, variant, customization);
    setAdded(true);
    toast.success(`${product.title} added to cart${customization ? " (customized)" : ""}`);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div data-testid="product-detail-page">
      <SEO
        title={`${product.title} — AVENO Shop | Julius Mason`}
        description={product.description || product.title}
        path={`/shop/product/${product.handle}`}
        image={product.image || undefined}
        lang="en"
        keywords={`${product.title}, AVENO, ${product.product_type}`}
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Shop", path: "/shop" },
          { name: product.title, path: `/shop/product/${product.handle}` },
        ])}
      />

      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Link to="/shop" data-testid="product-back-link"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to the shop
          </Link>

          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
            <Reveal>
              <div className="aspect-square overflow-hidden rounded-2xl bg-slate-50 border border-slate-100" data-testid="product-image">
                <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
              </div>
              {colorways.length > 0 && (
                <div className="mt-4" data-testid="colorway-gallery">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2.5">
                    Designs — currently viewing: <span className="text-primary">{pretty(designKey)}</span>
                  </p>
                  <div className="flex gap-2.5 overflow-x-auto pb-1">
                    <span className="w-16 h-16 rounded-lg overflow-hidden border-2 border-primary flex-shrink-0" title={pretty(designKey)}>
                      <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                    </span>
                    {colorways.map((c) => (
                      <Link
                        key={c.handle}
                        to={`/shop/product/${c.handle}`}
                        data-testid="colorway-thumb"
                        title={pretty(c.handle.split("-").pop())}
                        className="w-16 h-16 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-primary transition-colors flex-shrink-0"
                      >
                        <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex items-center gap-3 mb-4">
                <img src="/shop-images/aveno-logo.png" alt="" className="w-9 h-9 object-contain" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{product.vendor}</p>
              </div>
              <h1 className="font-display text-3xl md:text-5xl leading-tight mb-4" data-testid="product-title">{product.title}</h1>
              <p className="text-2xl font-semibold text-slate-900 mb-8" data-testid="product-detail-price">
                {formatMoney(product.price, data.currency)}
                {variant.compare_at_price && variant.compare_at_price > product.price && (
                  <span className="ml-3 text-base font-normal text-slate-400 line-through">
                    {formatMoney(variant.compare_at_price, data.currency)}
                  </span>
                )}
              </p>

              <button
                onClick={handleAdd}
                disabled={!product.available}
                data-testid="product-detail-add-cart"
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 font-semibold px-8 py-4 rounded-full transition-all active:scale-95 mb-10 ${
                  added ? "bg-emerald-600 text-white" : "bg-primary text-white hover:bg-primary/90"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {added ? <Check className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
                {product.available ? (added ? "Added to cart" : customization ? "Add customized to cart" : "Add to cart") : "Sold out"}
              </button>

              {isAveno && (
                <div className="mb-10" data-testid="customize-section">
                  <button
                    onClick={() => setCustomOpen(!customOpen)}
                    data-testid="customize-toggle"
                    className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 px-5 py-2.5 rounded-full hover:border-primary hover:text-primary transition-colors"
                  >
                    <Wand2 className="w-4 h-4" /> {customOpen ? "Hide customization" : "Customize this product"}
                  </button>
                  {customOpen && (
                    <div className="mt-4 border border-slate-200 rounded-2xl p-5 bg-slate-50/50 space-y-5" data-testid="customize-panel">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2.5">Design</p>
                        <div className="flex flex-wrap gap-2">
                          {AVENO_DESIGNS.map((d) => (
                            <button
                              key={d}
                              onClick={() => setCustomDesign(d)}
                              data-testid={`custom-design-${d.toLowerCase().replace(/\s+/g, "-")}`}
                              className={`text-xs font-medium px-3.5 py-2 rounded-full border transition-colors ${
                                (customDesign || pretty(designKey)) === d
                                  ? "border-primary bg-primary text-white"
                                  : "border-slate-300 hover:border-primary"
                              }`}
                            >
                              {d}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Custom text (optional)</p>
                        <input
                          data-testid="custom-text-input"
                          value={customText}
                          onChange={(e) => setCustomText(e.target.value)}
                          maxLength={60}
                          placeholder="e.g. a name, date or short quote"
                          className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary bg-white"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Your own artwork (optional)</p>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadArtwork(e.target.files[0])} data-testid="custom-artwork-input" />
                        {artwork ? (
                          <div className="flex items-center gap-3" data-testid="artwork-preview">
                            <img src={artwork.url} alt="Your artwork" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{artwork.name}</p>
                              <button onClick={() => { setArtwork(null); if (customDesign === "My own artwork") setCustomDesign(null); }} className="text-xs text-red-500 hover:underline">Remove</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid="artwork-upload-btn"
                            className="inline-flex items-center gap-2 text-sm border border-dashed border-slate-300 px-5 py-3.5 rounded-xl hover:border-primary hover:text-primary transition-colors w-full justify-center bg-white disabled:opacity-50"
                          >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {uploading ? "Uploading…" : "Upload an image (PNG, JPG — max 15 MB)"}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Customized products are made to order. Your design choice, text and artwork travel
                        with the order — I confirm every custom order by email before printing.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {product.description_html && (
                <div
                  className="prose prose-slate max-w-none text-slate-600 leading-relaxed mb-10 [&_ul]:list-disc [&_ul]:pl-5 [&_h2]:font-display [&_h2]:text-xl [&_h2]:mt-6 [&_h3]:font-display [&_h3]:text-lg [&_h3]:mt-5"
                  data-testid="product-description"
                  dangerouslySetInnerHTML={{ __html: product.description_html }}
                />
              )}

              <div className="border-t border-slate-100 pt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2.5">
                  <Truck className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Tracked shipping, calculated at checkout</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <RotateCcw className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">30-day easy returns</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Secure checkout via Shopify</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Cinematic ad video for this product (AI-generated by the AVENO engine) */}
      {videos?.landscape && (
        <section className="py-16 border-t border-slate-100 bg-secondary/20" data-testid="product-video-section">
          <div className="mx-auto max-w-[1200px] px-6 md:px-12">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">In Motion</p>
            <h2 className="font-display text-3xl mb-6">See it come alive</h2>
            <div className="border border-border bg-black">
              <video src={videos.landscape} autoPlay muted loop playsInline controls preload="metadata"
                     className="w-full max-h-[540px] object-contain" data-testid="product-video" />
            </div>
          </div>
        </section>
      )}

      {/* Related — same design family */}
      {family.length > 0 && (
        <section className="py-16 border-t border-slate-100" data-testid="related-products">
          <div className="mx-auto max-w-[1200px] px-6 md:px-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-3">Same design family</p>
            <h2 className="font-display text-2xl md:text-3xl mb-8">More from the {pretty(designKey)} collection</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {family.map((r) => (
                <Link key={r.handle} to={`/shop/product/${r.handle}`} className="group block" data-testid="related-product-card">
                  <div className="aspect-[4/3] overflow-hidden rounded-xl bg-slate-50 border border-slate-100 mb-3">
                    <img src={r.image} alt={r.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-semibold text-sm group-hover:text-primary transition-colors leading-snug">{r.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{formatMoney(r.price, data.currency)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CartDrawer checkoutBase={data?.checkout_base} currency={data?.currency} />
    </div>
  );
}
